import { isEmpty } from './is_empty'
import { isObject, MAX_RECURSION_DEPTH_MEDIUM } from './common_utils'
import { safeJsonParse } from './safe_json_parse'
import { cloneImmutable } from './clone_immutable'

/**
 * This function help to recurse object without changing the original reference
 * this function complexity is O(log n)
 * @param request_
 * @param maxDepth
 * @returns
 */

export const objectRecurseImmutable = <T>(
  request_: {
    target: T
    /** @description !don't do recursion inside */
    doOnValue?: (value: unknown, key: string, owner: unknown, depth: {
      maxDepth: number
      depth: number
    }) => void

    /** @description !don't do recursion inside */
    doOnObject?: (object: unknown, key: string, owner: unknown, depth: {
      maxDepth: number
      depth: number
    }) => void

    /** @description !don't do recursion inside */
    doOnResult?: (target: unknown, depth: { maxDepth: number; depth: number }) => unknown

    /** @description !don't do recursion inside */
    doOnDepthReached?: (target: unknown, depth: { maxDepth: number; depth: number }) => unknown

    /** @default true */
    gcTargetAfterDone?: boolean
  },
  maxDepth = MAX_RECURSION_DEPTH_MEDIUM,
): T | undefined => {
  const { doOnValue, doOnObject, doOnResult, doOnDepthReached, target, gcTargetAfterDone } = request_ ?? {}
  const _visit = (
    visitTarget: unknown,
    visitMaxDepth: number,
  ) => {
    if (visitMaxDepth < 1) {
      return doOnDepthReached?.(visitTarget, {
        depth: maxDepth - visitMaxDepth,
        maxDepth: visitMaxDepth,
      }) ?? visitTarget
    }

    if (isEmpty(visitTarget))
      return void 0

    if (isObject(visitTarget)) {
      for (const [key, value] of Object.entries(visitTarget)) {
        if (isObject(value)) {
          doOnObject?.(value, key, visitTarget, {
            depth: maxDepth - visitMaxDepth,
            maxDepth: visitMaxDepth,
          })
          visitTarget[key] = _visit(
            visitTarget[key],
            visitMaxDepth - 1,
          )
        } else {
          doOnValue?.(value, key, visitTarget, {
            depth: maxDepth - visitMaxDepth,
            maxDepth: visitMaxDepth,
          })
        }
      }
    }

    visitTarget = doOnResult?.(visitTarget, {
      depth: maxDepth - visitMaxDepth,
      maxDepth: visitMaxDepth,
    }) ?? visitTarget

    return visitTarget
  }

  try {
    // Make the target immutable, so it wont change the original reference
    const visited = _visit(cloneImmutable(target), maxDepth) as T
    if (gcTargetAfterDone)
      request_.target = void 0 as any // GC
    return visited
  } catch (error) {
    console.error(`objectRecurseImmutable:FatalErr`)
    console.error({ error })
  }
}
